import React, { useMemo } from 'react';

import { Box } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import InputWithDebouncedAction from 'components/InputWithDebouncedAction/InputWithDebouncedAction';
import processing_activities_messages from 'messages/processing_activities_messages';
import ProcessingActivitiesFilterSelect from 'pages/ProcessingActivities/ProcessingActivitiesPage/_components/ProcessingActivitiesFilterSelect';

import type { AssetRopaReviewFilterOptions, AssetRopaReviewFilters } from '../assetRopaReview.types';
import useStyles from '../ProcessingActivitiesAssetReviewsPage.styles';

type ReviewStatusMessageKey = keyof typeof processing_activities_messages.asset_ropa.review_status;

const REVIEW_STATUS_OPTIONS = Object.keys(processing_activities_messages.asset_ropa.review_status) as ReviewStatusMessageKey[];
const WARNING_STATE_OPTIONS = ['with_warnings', 'no_warnings'] as const;
const DPIA_STATE_OPTIONS = ['indicated', 'required_unapproved', 'none'] as const;
const BOOLEAN_FILTER_OPTIONS = ['true', 'false'] as const;

type Props = {
  filterOptions: AssetRopaReviewFilterOptions;
  filters: AssetRopaReviewFilters;
  onFiltersChange: (filters: AssetRopaReviewFilters) => void;
  onSearch: (value: string) => void;
  searchValue: string;
};

const AssetRopaFiltersToolbar: React.FC<Props> = ({ filterOptions, filters, onFiltersChange, onSearch, searchValue }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const filterMessages = processing_activities_messages.asset_ropa.filters;
  const formatFilterAllLabel = (label: string) => t(processing_activities_messages.filters.option_all, { label });

  const reviewStatusOptions = useMemo(
    () =>
      REVIEW_STATUS_OPTIONS.map(status => ({
        value: status,
        label: t(processing_activities_messages.asset_ropa.review_status[status]),
      })),
    [t],
  );

  const assetTypeOptions = useMemo(
    () =>
      filterOptions.assetTypes.map(assetType => ({
        value: String(assetType.id),
        label: assetType.name,
      })),
    [filterOptions.assetTypes],
  );

  const operationalOwnerOptions = useMemo(
    () =>
      filterOptions.operationalOwners.map(owner => ({
        value: String(owner.id),
        label: owner.name,
      })),
    [filterOptions.operationalOwners],
  );

  const warningStateOptions = useMemo(
    () =>
      WARNING_STATE_OPTIONS.map(value => ({
        value,
        label: t(filterMessages[value]),
      })),
    [filterMessages, t],
  );

  const dpiaStateOptions = useMemo(() => {
    const messageKeyByValue = {
      indicated: filterMessages.dpia_indicated,
      required_unapproved: filterMessages.dpia_required_unapproved,
      none: filterMessages.dpia_none,
    } as const;

    return DPIA_STATE_OPTIONS.map(value => ({
      value,
      label: t(messageKeyByValue[value]),
    }));
  }, [filterMessages, t]);

  const booleanFilterOptions = useMemo(
    () =>
      BOOLEAN_FILTER_OPTIONS.map(value => ({
        value,
        label: t(filterMessages[value === 'true' ? 'yes' : 'no']),
      })),
    [filterMessages, t],
  );

  return (
    <Box className={styles.toolbarSection}>
      <Box className={styles.filtersRow}>
        <div className={styles.searchInputCell}>
          <InputWithDebouncedAction
            action={onSearch}
            formControl={{ size: 'small' }}
            label={t(filterMessages.search_placeholder)}
            value={searchValue}
          />
        </div>

        <ProcessingActivitiesFilterSelect
          allLabel={formatFilterAllLabel(t(filterMessages.review_status))}
          ariaLabel={t(filterMessages.review_status)}
          onChange={reviewStatus => onFiltersChange({ ...filters, reviewStatus: reviewStatus as AssetRopaReviewFilters['reviewStatus'] })}
          options={reviewStatusOptions}
          value={filters.reviewStatus}
        />

        <ProcessingActivitiesFilterSelect
          allLabel={formatFilterAllLabel(t(filterMessages.asset_type))}
          ariaLabel={t(filterMessages.asset_type)}
          onChange={assetTypeId =>
            onFiltersChange({
              ...filters,
              assetTypeId: assetTypeId ? Number(assetTypeId) : '',
            })
          }
          options={assetTypeOptions}
          value={filters.assetTypeId === '' ? '' : String(filters.assetTypeId)}
        />

        <ProcessingActivitiesFilterSelect
          allLabel={formatFilterAllLabel(t(filterMessages.operational_owner))}
          ariaLabel={t(filterMessages.operational_owner)}
          onChange={operationalOwnerMembershipId =>
            onFiltersChange({
              ...filters,
              operationalOwnerMembershipId: operationalOwnerMembershipId ? Number(operationalOwnerMembershipId) : '',
            })
          }
          options={operationalOwnerOptions}
          value={filters.operationalOwnerMembershipId === '' ? '' : String(filters.operationalOwnerMembershipId)}
        />

        <ProcessingActivitiesFilterSelect
          allLabel={formatFilterAllLabel(t(filterMessages.warning_state))}
          ariaLabel={t(filterMessages.warning_state)}
          onChange={warningState => onFiltersChange({ ...filters, warningState: warningState as AssetRopaReviewFilters['warningState'] })}
          options={warningStateOptions}
          value={filters.warningState}
        />

        <ProcessingActivitiesFilterSelect
          allLabel={formatFilterAllLabel(t(filterMessages.dpia_state))}
          ariaLabel={t(filterMessages.dpia_state)}
          onChange={dpiaState => onFiltersChange({ ...filters, dpiaState: dpiaState as AssetRopaReviewFilters['dpiaState'] })}
          options={dpiaStateOptions}
          value={filters.dpiaState}
        />

        <ProcessingActivitiesFilterSelect
          allLabel={formatFilterAllLabel(t(filterMessages.sensitive_personal_data))}
          ariaLabel={t(filterMessages.sensitive_personal_data)}
          onChange={sensitivePersonalData =>
            onFiltersChange({
              ...filters,
              sensitivePersonalData: sensitivePersonalData as AssetRopaReviewFilters['sensitivePersonalData'],
            })
          }
          options={booleanFilterOptions}
          value={filters.sensitivePersonalData}
        />

        <ProcessingActivitiesFilterSelect
          allLabel={formatFilterAllLabel(t(filterMessages.third_country_processing))}
          ariaLabel={t(filterMessages.third_country_processing)}
          onChange={thirdCountryProcessing =>
            onFiltersChange({
              ...filters,
              thirdCountryProcessing: thirdCountryProcessing as AssetRopaReviewFilters['thirdCountryProcessing'],
            })
          }
          options={booleanFilterOptions}
          value={filters.thirdCountryProcessing}
        />
      </Box>
    </Box>
  );
};

export default AssetRopaFiltersToolbar;
